import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, TextField, CircularProgress } from '@mui/material'
import { PhoneInput, parseCountry, defaultCountries } from 'react-international-phone'
import 'react-international-phone/style.css'
import { useTranslation } from 'react-i18next'
import { updateUserProfile } from '../../redux/actions/authAction'
import { updateAppPreferences } from '../../redux/actions/userAction'
import { setTheme } from '../../redux/actions/themeActions'
import { useUserTypes } from '../../hooks/useUserTypes'

const GOLD = '#c9a96e'
const GOLD_DARK = '#a07840'

/* ─── Theme-aware palette ──────────────────────────────────────── */
const palette = (isDark) => ({
  bg: isDark ? '#000' : '#f5f5f4',
  text: isDark ? '#fff' : '#1c1917',
  textMuted: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
  textSubtle: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
  textFaint: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
  textHover: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
  border: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
  borderHover: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
  borderRadio: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
  inputBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
  autofillBg: isDark ? '#0d0d0d' : '#f5f5f4',
  cardBg: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
  btnBg: isDark ? '#fff' : '#1c1917',
  btnText: isDark ? '#000' : '#fff',
  btnDisabledBg: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
  btnDisabledText: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
  btnHoverBg: isDark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.82)',
  grain: isDark ? 0.025 : 0.015,
  glow: isDark ? 'rgba(201,169,110,0.04)' : 'rgba(201,169,110,0.06)',
  progressBg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)',
  dropdownBg: isDark ? '#1a1a1a' : '#fff',
  dropdownBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  dropdownItemText: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
  dropdownSelectedBg: isDark ? 'rgba(201,169,110,0.18)' : 'rgba(201,169,110,0.15)',
  dropdownDialCode: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
  dropdownArrow: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
  goldSelectedBg: isDark ? 'rgba(201,169,110,0.12)' : 'rgba(201,169,110,0.1)',
  goldHoverBg: isDark ? 'rgba(201,169,110,0.06)' : 'rgba(201,169,110,0.08)',
})

/* ─── Shared input style ────────────────────────────────────────── */
const getInputSx = (p) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: p.inputBg,
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '16px',
    color: p.text,
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: p.border, transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: p.borderHover },
    '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: '1.5px' },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px ${p.autofillBg} inset`,
      WebkitTextFillColor: p.text,
      caretColor: p.text,
      borderRadius: '14px',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: '"DM Sans", sans-serif',
    color: p.textMuted,
    fontSize: '14px',
    '&.Mui-focused': { color: GOLD },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: '"DM Sans", sans-serif',
    color: '#f87171',
  },
})

/* ─── Keyframes ─────────────────────────────────────────────────── */
const globalStyles = `
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(28px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-28px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`

const TOTAL_STEPS = 5

/* ─── Main component ────────────────────────────────────────────── */
export default function OnboardingScreen() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const userData = useSelector(state => state.auth?.user?.user)
  const themeMode = useSelector(state => state.theme?.mode || 'dark')
  const { groups: userTypeGroups } = useUserTypes()
  const isDark = themeMode === 'dark'
  const p = palette(isDark)

  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState('forward')
  const [animKey, setAnimKey] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Pre-fill from Redux — skip placeholder org names (invite-org creates "Organization of ...")
  const rawOrgName = userData?.organization_name || ''
  const isPlaceholderOrg = rawOrgName.startsWith('Organization of ')
  const [formData, setFormData] = useState({
    theme_mode: 'dark',
    first_name: userData?.first_name || '',
    last_name: userData?.last_name || '',
    organization_name: isPlaceholderOrg ? '' : rawOrgName,
    phone_number: userData?.phone_number || '',
    user_type: userData?.user_type || '',
  })
  const [errors, setErrors] = useState({})
  const firstInputRef = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstInputRef.current) firstInputRef.current.focus()
    }, 260)
    return () => clearTimeout(timer)
  }, [step])

  const goNext = () => {
    setDirection('forward')
    setAnimKey(k => k + 1)
    setStep(s => s + 1)
  }

  const goBack = () => {
    if (step === 0) return
    setDirection('back')
    setAnimKey(k => k + 1)
    setStep(s => s - 1)
  }

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }))
  }

  const validateStep = () => {
    if (step === 1) {
      const e = {}
      if (!formData.first_name.trim()) e.first_name = t('onboarding_field_required')
      if (!formData.last_name.trim()) e.last_name = t('onboarding_field_required')
      if (Object.keys(e).length) { setErrors(e); return false }
    }
    if (step === 3) {
      // Phone value includes dial code (e.g. +212...), need at least 8 local digits beyond it
      const digits = formData.phone_number.replace(/\D/g, '')
      if (digits.length < 10) {
        setErrors({ phone_number: t('onboarding_phone_invalid') })
        return false
      }
    }
    return true
  }

  const handleContinue = () => {
    if (!validateStep()) return
    if (step < TOTAL_STEPS - 1) {
      goNext()
    } else {
      handleSubmit()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleContinue()
  }

  const handleThemeSelect = (mode) => {
    updateField('theme_mode', mode)
    dispatch(setTheme(mode))
  }

  const handleUserTypeSelect = (value) => {
    updateField('user_type', value)
    setTimeout(() => {
      submitProfile({ ...formData, user_type: value })
    }, 220)
  }

  const handleSubmit = () => submitProfile(formData)

  const submitProfile = async (payload) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      await dispatch(updateUserProfile({
        first_name: payload.first_name.trim(),
        last_name: payload.last_name.trim(),
        phone_number: payload.phone_number.trim(),
        user_type: payload.user_type,
        organization_name: payload.organization_name.trim() || null,
      }, navigate))
      // Persist theme preference to backend for cross-device sync
      dispatch(updateAppPreferences({ theme_mode: payload.theme_mode }))
    } catch {
      setSubmitError(t('onboarding_submit_error'))
      setSubmitting(false)
    }
  }

  const stepAnim = direction === 'forward'
    ? 'slideInRight 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards'
    : 'slideInLeft 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards'

  const progressPct = (step / TOTAL_STEPS) * 100

  return (
    <>
      <style>{globalStyles}</style>

      <Box sx={{ minHeight: '100vh', backgroundColor: p.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', transition: 'background-color 0.4s ease' }}>
        {/* Grain */}
        <Box sx={{ position: 'fixed', inset: 0, opacity: p.grain, pointerEvents: 'none', zIndex: 0, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '256px 256px' }} />
        {/* Glow */}
        <Box sx={{ position: 'fixed', inset: 0, background: `radial-gradient(ellipse 60% 50% at 50% 50%, ${p.glow} 0%, transparent 70%)`, pointerEvents: 'none', zIndex: 0 }} />

        {/* Progress bar */}
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: '2px', backgroundColor: p.progressBg, zIndex: 10 }}>
          <Box sx={{ height: '100%', width: `${progressPct}%`, background: `linear-gradient(90deg, ${GOLD_DARK}, ${GOLD})`, transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }} />
        </Box>

        {/* Back button */}
        {step > 0 && !submitting && (
          <Box component="button" onClick={goBack} sx={{ position: 'fixed', top: 24, left: 28, background: 'none', border: 'none', color: p.textMuted, fontFamily: '"DM Sans", sans-serif', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, zIndex: 10, transition: 'color 0.2s ease', '&:hover': { color: p.textHover } }}>
            {t('onboarding_back')}
          </Box>
        )}

        {/* Step counter */}
        <Box sx={{ position: 'fixed', top: 24, right: 28, zIndex: 10 }}>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '12px', color: p.textFaint, letterSpacing: '0.1em' }}>
            {step + 1} / {TOTAL_STEPS}
          </Typography>
        </Box>

        {/* Card */}
        <Box sx={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: (step === 0 || step === 4) ? '760px' : '500px', px: { xs: 3, sm: 4 }, transition: 'max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          {submitting ? (
            <SubmittingState label={t('onboarding_submitting')} p={p} />
          ) : (
            <Box key={animKey} sx={{ animation: stepAnim }}>
              {step === 0 && (
                <StepTheme
                  formData={formData} onSelect={handleThemeSelect}
                  onContinue={handleContinue} t={t} p={p}
                />
              )}
              {step === 1 && (
                <StepName
                  formData={formData} errors={errors} updateField={updateField}
                  onContinue={handleContinue} onKeyDown={handleKeyDown}
                  firstInputRef={firstInputRef} t={t} p={p}
                />
              )}
              {step === 2 && (
                <StepOrganization
                  formData={formData} updateField={updateField}
                  onContinue={handleContinue} onSkip={goNext}
                  onKeyDown={handleKeyDown} firstInputRef={firstInputRef} t={t} p={p}
                />
              )}
              {step === 3 && (
                <StepPhone
                  formData={formData} errors={errors} updateField={updateField}
                  onContinue={handleContinue} onKeyDown={handleKeyDown}
                  firstInputRef={firstInputRef} t={t} p={p}
                />
              )}
              {step === 4 && (
                <StepUserType formData={formData} onSelect={handleUserTypeSelect} t={t} groups={userTypeGroups} p={p} />
              )}

              {submitError && (
                <Typography sx={{ mt: 2, fontFamily: '"DM Sans", sans-serif', fontSize: '13px', color: '#f87171', textAlign: 'center', transition: 'color 0.3s ease' }}>
                  {submitError}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

/* ─── Submitting ────────────────────────────────────────────────── */
function SubmittingState({ label, p }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, animation: 'fadeInUp 0.4s ease forwards' }}>
      <CircularProgress size={40} thickness={2} sx={{ color: GOLD }} />
      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '15px', color: p.textMuted }}>
        {label}
      </Typography>
    </Box>
  )
}

/* ─── Shared heading ────────────────────────────────────────────── */
function StepHeading({ question, hint, p }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300, fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', color: p.text, letterSpacing: '-0.02em', lineHeight: 1.15, mb: hint ? 1.5 : 0, transition: 'color 0.3s ease' }}>
        {question}
      </Typography>
      {hint && (
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '14px', color: p.textMuted, lineHeight: 1.6, transition: 'color 0.3s ease' }}>
          {hint}
        </Typography>
      )}
      <Box sx={{ width: 32, height: 1.5, backgroundColor: GOLD, opacity: 0.5, mt: 2, borderRadius: 1 }} />
    </Box>
  )
}

/* ─── Continue button ───────────────────────────────────────────── */
function ContinueButton({ onClick, disabled, label, p }) {
  return (
    <Box component="button" onClick={onClick} disabled={disabled} sx={{ mt: 3, width: '100%', py: 1.7, borderRadius: '14px', border: 'none', backgroundColor: disabled ? p.btnDisabledBg : p.btnBg, color: disabled ? p.btnDisabledText : p.btnText, fontFamily: '"DM Sans", sans-serif', fontSize: '14px', fontWeight: 700, letterSpacing: '0.04em', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', '&:hover:not(:disabled)': { backgroundColor: p.btnHoverBg, transform: 'translateY(-1px)' }, '&:active:not(:disabled)': { transform: 'translateY(0)' } }}>
      {label}
    </Box>
  )
}

/* ─── Step 0: Theme ─────────────────────────────────────────────── */
function StepTheme({ formData, onSelect, onContinue, t, p }) {
  const selected = formData.theme_mode || 'dark'

  const themes = [
    {
      key: 'dark',
      label: t('onboarding_theme_dark'),
      bg: '#0a0a0a',
      cardBg: '#141414',
      sidebar: '#0f0f0f',
      text: '#e5e5e5',
      textMuted: 'rgba(255,255,255,0.35)',
      border: 'rgba(255,255,255,0.06)',
      accentBar: GOLD,
    },
    {
      key: 'light',
      label: t('onboarding_theme_light'),
      bg: '#f5f5f4',
      cardBg: '#ffffff',
      sidebar: '#fafaf9',
      text: '#1c1917',
      textMuted: 'rgba(0,0,0,0.35)',
      border: 'rgba(0,0,0,0.08)',
      accentBar: GOLD,
    },
  ]

  return (
    <>
      <StepHeading question={t('onboarding_step_theme_question')} hint={t('onboarding_step_theme_hint')} p={p} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
        {themes.map((theme) => {
          const isSelected = selected === theme.key
          return (
            <Box
              key={theme.key}
              component="button"
              onClick={() => onSelect(theme.key)}
              sx={{
                background: p.cardBg,
                border: isSelected ? `2px solid ${GOLD}` : `2px solid ${p.border}`,
                borderRadius: '18px',
                p: 2.5,
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                position: 'relative',
                overflow: 'hidden',
                textAlign: 'left',
                '&:hover': {
                  border: `2px solid ${isSelected ? GOLD : 'rgba(201,169,110,0.4)'}`,
                  transform: 'translateY(-2px)',
                  boxShadow: isSelected ? `0 8px 32px rgba(201,169,110,0.12)` : '0 8px 24px rgba(0,0,0,0.2)',
                },
                '&:active': { transform: 'translateY(0)' },
                ...(isSelected && {
                  boxShadow: `0 0 0 1px rgba(201,169,110,0.15), 0 8px 32px rgba(201,169,110,0.08)`,
                }),
              }}
            >
              {/* Mini UI Preview */}
              <Box sx={{
                borderRadius: '10px',
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.bg,
                height: 160,
                display: 'flex',
                mb: 2,
              }}>
                {/* Mini Sidebar */}
                <Box sx={{
                  width: 52,
                  backgroundColor: theme.sidebar,
                  borderRight: `1px solid ${theme.border}`,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.8,
                  pt: 1.5,
                }}>
                  {/* Logo dot */}
                  <Box sx={{ width: 16, height: 16, borderRadius: '4px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`, mb: 1, mx: 'auto' }} />
                  {/* Nav items */}
                  {[0.6, 0.45, 0.55, 0.4].map((w, i) => (
                    <Box key={i} sx={{
                      width: `${w * 100}%`,
                      height: 5,
                      borderRadius: 3,
                      backgroundColor: i === 0 ? `${GOLD}30` : theme.textMuted,
                      opacity: i === 0 ? 1 : 0.3,
                      mx: 'auto',
                    }} />
                  ))}
                </Box>
                {/* Mini Content Area */}
                <Box sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Top bar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                    <Box sx={{ width: '35%', height: 5, borderRadius: 3, backgroundColor: theme.text, opacity: 0.15 }} />
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: theme.text, opacity: 0.08 }} />
                  </Box>
                  {/* Content cards */}
                  {[0, 1].map((i) => (
                    <Box key={i} sx={{
                      backgroundColor: theme.cardBg,
                      borderRadius: '6px',
                      border: `1px solid ${theme.border}`,
                      p: 1,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: i === 0 ? GOLD : theme.text, opacity: i === 0 ? 0.6 : 0.1 }} />
                        <Box sx={{ width: '55%', height: 4, borderRadius: 2, backgroundColor: theme.text, opacity: 0.2 }} />
                      </Box>
                      <Box sx={{ width: '80%', height: 3, borderRadius: 2, backgroundColor: theme.text, opacity: 0.08 }} />
                      <Box sx={{ width: '60%', height: 3, borderRadius: 2, backgroundColor: theme.text, opacity: 0.06 }} />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Radio indicator */}
                <Box sx={{
                  width: 20, height: 20, borderRadius: '50%',
                  border: isSelected ? `2px solid ${GOLD}` : `2px solid ${p.borderRadio}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}>
                  {isSelected && (
                    <Box sx={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`,
                    }} />
                  )}
                </Box>
                <Typography sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '15px',
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? p.text : p.textSubtle,
                  letterSpacing: '0.01em',
                  transition: 'color 0.2s ease',
                }}>
                  {theme.label}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
      <ContinueButton onClick={onContinue} label={t('onboarding_continue')} p={p} />
    </>
  )
}

/* ─── Step 1: Name ──────────────────────────────────────────────── */
function StepName({ formData, errors, updateField, onContinue, onKeyDown, firstInputRef, t, p }) {
  const isx = getInputSx(p)
  return (
    <>
      <StepHeading question={t('onboarding_step_name_question')} hint={t('onboarding_step_name_hint')} p={p} />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField fullWidth label={t('onboarding_first_name')} value={formData.first_name} onChange={e => updateField('first_name', e.target.value)} onKeyDown={onKeyDown} error={!!errors.first_name} helperText={errors.first_name} inputRef={firstInputRef} sx={isx} />
        <TextField fullWidth label={t('onboarding_last_name')} value={formData.last_name} onChange={e => updateField('last_name', e.target.value)} onKeyDown={onKeyDown} error={!!errors.last_name} helperText={errors.last_name} sx={isx} />
      </Box>
      <ContinueButton onClick={onContinue} disabled={!formData.first_name.trim() || !formData.last_name.trim()} label={t('onboarding_continue')} p={p} />
    </>
  )
}

/* ─── Step 2: Organization ──────────────────────────────────────── */
function StepOrganization({ formData, updateField, onContinue, onSkip, onKeyDown, firstInputRef, t, p }) {
  const isx = getInputSx(p)
  return (
    <>
      <StepHeading question={t('onboarding_step_org_question')} hint={t('onboarding_step_org_hint')} p={p} />
      <TextField fullWidth label={t('onboarding_org_name')} value={formData.organization_name} onChange={e => updateField('organization_name', e.target.value)} onKeyDown={onKeyDown} inputRef={firstInputRef} sx={isx} />
      <ContinueButton onClick={onContinue} label={t('onboarding_continue')} p={p} />
      <Box sx={{ mt: 1.5, textAlign: 'center' }}>
        <Box component="button" onClick={onSkip} sx={{ background: 'none', border: 'none', color: p.textFaint, fontFamily: '"DM Sans", sans-serif', fontSize: '13px', cursor: 'pointer', transition: 'color 0.2s ease', '&:hover': { color: p.textSubtle } }}>
          {t('onboarding_skip')}
        </Box>
      </Box>
    </>
  )
}

/* ─── Step 3: Phone ─────────────────────────────────────────────── */
function StepPhone({ formData, errors, updateField, onContinue, onKeyDown, firstInputRef, t, p }) {
  const [country, setCountry] = useState('ma')

  // Get dial code length for current country
  const countryData = defaultCountries.find(c => parseCountry(c).iso2 === country)
  const dialCode = countryData ? parseCountry(countryData).dialCode : ''
  const digitsOnly = formData.phone_number.replace(/\D/g, '')
  // Local digits = total digits minus dial code
  const localDigits = digitsOnly.startsWith(dialCode) ? digitsOnly.slice(dialCode.length) : digitsOnly
  const isComplete = localDigits.length >= 9

  return (
    <>
      <StepHeading question={t('onboarding_step_phone_question')} hint={t('onboarding_step_phone_hint')} p={p} />
      <Box
        onKeyDown={onKeyDown}
        sx={{
          '& .react-international-phone-input-container': {
            width: '100%',
            height: '56px',
            display: 'flex',
            alignItems: 'stretch',
            borderRadius: '14px',
            backgroundColor: `${p.inputBg} !important`,
            border: errors.phone_number ? '1.5px solid #f87171' : `1px solid ${p.border}`,
            transition: 'all 0.2s ease',
            overflow: 'hidden',
            '&:hover': { borderColor: errors.phone_number ? '#f87171' : p.borderHover },
            '&:focus-within': { borderColor: errors.phone_number ? '#f87171' : GOLD, borderWidth: '1.5px' },
          },
          '& .react-international-phone-country-selector-button': {
            height: '56px',
            background: 'none !important',
            backgroundColor: 'transparent !important',
            border: 'none !important',
            borderRight: `1px solid ${p.border} !important`,
            borderRadius: '0 !important',
            paddingLeft: '14px',
            paddingRight: '12px',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: `${p.inputBg} !important` },
          },
          '& .react-international-phone-country-selector-button__button-content': {
            gap: '8px',
          },
          '& .react-international-phone-country-selector-button__dropdown-arrow': {
            borderTop: `4px solid ${p.dropdownArrow}`,
          },
          '& .react-international-phone-country-selector-button__flag-emoji, & .react-international-phone-flag-emoji': {
            fontSize: '20px',
          },
          '& .react-international-phone-input': {
            flex: '1 !important',
            height: '56px !important',
            background: 'none !important',
            backgroundColor: 'transparent !important',
            border: 'none !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '16px !important',
            color: `${p.text} !important`,
            paddingLeft: '14px',
            '&::placeholder': { color: p.textFaint },
            '&:focus': { background: 'none !important', backgroundColor: 'transparent !important' },
          },
          '& .react-international-phone-country-selector-dropdown': {
            backgroundColor: p.dropdownBg,
            border: `1px solid ${p.dropdownBorder}`,
            borderRadius: '12px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            maxHeight: '280px',
            zIndex: 100,
          },
          '& .react-international-phone-country-selector-dropdown__list-item': {
            color: p.dropdownItemText,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '14px',
            padding: '10px 14px',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: 'rgba(201,169,110,0.12)' },
          },
          '& .react-international-phone-country-selector-dropdown__list-item--selected': {
            backgroundColor: p.dropdownSelectedBg,
            color: p.text,
          },
          '& .react-international-phone-country-selector-dropdown__list-item-dial-code': {
            color: p.dropdownDialCode,
          },
          '& .react-international-phone-country-selector-dropdown__search': {
            backgroundColor: p.dropdownBg,
            color: p.text,
            border: 'none',
            borderBottom: `1px solid ${p.border}`,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '14px',
            padding: '12px 14px',
            '&::placeholder': { color: p.textFaint },
          },
        }}
      >
        <PhoneInput
          defaultCountry="ma"
          value={formData.phone_number}
          onChange={(phone, meta) => {
            updateField('phone_number', phone)
            if (meta?.country?.iso2) setCountry(meta.country.iso2)
          }}
          inputRef={firstInputRef}
          inputProps={{ autoFocus: true }}
        />
      </Box>
      {errors.phone_number && (
        <Typography sx={{ mt: 0.5, ml: 1.5, fontFamily: '"DM Sans", sans-serif', fontSize: '12px', color: '#f87171' }}>
          {errors.phone_number}
        </Typography>
      )}
      <ContinueButton onClick={onContinue} disabled={!isComplete} label={t('onboarding_continue')} p={p} />
    </>
  )
}

/* ─── Step 4: User Type ─────────────────────────────────────────── */
function StepUserType({ formData, onSelect, t, groups, p }) {
  const [selected, setSelected] = useState(formData.user_type || '')

  const handleClick = (value) => {
    setSelected(value)
    onSelect(value)
  }

  return (
    <>
      <StepHeading question={t('onboarding_step_profile_question')} hint={t('onboarding_step_profile_hint')} p={p} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {groups.map((group) => (
          <Box key={group.key}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '10px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: p.textFaint, mb: 1.5, transition: 'color 0.3s ease' }}>
              {group.label}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
              {group.items.map((item) => {
                const isSelected = selected === item.value
                return (
                  <Box key={item.value} component="button" onClick={() => handleClick(item.value)} sx={{ background: isSelected ? p.goldSelectedBg : p.cardBg, border: isSelected ? `1.5px solid ${GOLD}` : `1.5px solid ${p.border}`, borderRadius: '12px', px: 1.5, py: 1.5, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, transition: 'all 0.18s ease', textAlign: 'left', '&:hover': { border: '1.5px solid rgba(201,169,110,0.4)', background: p.goldHoverBg, transform: 'translateY(-1px)' }, '&:active': { transform: 'translateY(0)' } }}>
                    <Typography sx={{ fontSize: '18px', lineHeight: 1 }}>{item.icon}</Typography>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '12px', fontWeight: isSelected ? 600 : 400, color: isSelected ? p.text : p.textSubtle, lineHeight: 1.3, transition: 'color 0.18s ease' }}>
                      {item.label}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  )
}
